<template>
    <v-container fluid>
        <v-card class="leads-table-card">
            <div class="card-section-header px-6 py-4">
                <div class="d-flex align-center flex-wrap" style="gap:10px;">
                    <v-text-field outlined dense hide-details placeholder="Search name, email or mobile"
                        prepend-inner-icon="mdi-magnify" v-model="search_query" clearable style="width:300px;"
                        class="header-filter-input">
                    </v-text-field>
                    <v-select outlined dense hide-details placeholder="All Advisors" v-model="filter_advisor"
                        :items="advisorOptions" item-text="name" item-value="id" clearable style="width:200px;"
                        class="header-filter-input">
                    </v-select>
                    <v-select outlined dense hide-details placeholder="All Event Types" v-model="filter_event_type"
                        :items="eventTypeOptions" clearable style="width:200px;" class="header-filter-input">
                    </v-select>
                    <v-select outlined dense hide-details placeholder="All Destinations"
                        v-model="filter_destination" :items="destinationOptions" clearable style="width:200px;"
                        class="header-filter-input">
                    </v-select>
                    <v-select outlined dense hide-details placeholder="All Paid Status" v-model="filter_paid"
                        :items="paidOptions" item-text="text" item-value="value" clearable style="width:200px;"
                        class="header-filter-input">
                    </v-select>
                    <v-spacer></v-spacer>
                    <v-btn outlined depressed class="leads-header-btn header-outline-btn" @click="resetAndFetch"
                        :loading="leads_loading">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                    <v-btn depressed class="leads-header-btn header-create-btn"
                        @click="eventOpenCreateNewLeadDialog()">
                        <v-icon left>mdi-plus</v-icon>
                        Create
                    </v-btn>
                </div>
                <div v-if="hasActiveFilters" class="header-clear-row">
                    <v-btn x-small text class="header-clear-btn" @click="clearFilters">
                        <v-icon left x-small>mdi-close</v-icon>
                        Clear filters
                    </v-btn>
                </div>
            </div>

            <!-- Table -->
            <v-card-text class="pa-0">
                <v-data-table :headers="headers" :items="leads" :items-per-page="15" class="leads-table"
                    :loading="leads_loading" loading-text="Loading leads..." :server-items-length="leads_total"
                    hide-default-footer>
                    <template v-slot:item="{ item }">
                        <tr class="lead-row" @click="eventMoveToEdit(item)">
                            <td class="text-center grey--text caption">{{ item.id }}</td>
                            <td class="caption">{{ $date(item.created_at).format('DD/MM/YYYY') }}</td>
                            <td class="font-weight-medium">
                                <a :href="$router.resolve({ name: 'EditLeads', params: { id: item.id } }).href"
                                    class="lead-name-link" @click.prevent.stop="eventMoveToEdit(item)">{{ item.first_name }} {{ item.last_name }}</a>
                            </td>
                            <td>{{ item.phone }}</td>
                            <td>{{ item.email }}</td>
                            <td>{{ $date(item.start_date).format('DD/MM/YYYY') }}</td>
                            <td class="text-center">{{ item.people }}</td>
                            <td>
                                <v-chip x-small label v-if="item.event_type.name"
                                    :color="item.event_type.name === 'Hen' ? 'pink lighten-4' : item.event_type.name === 'Birthday' ? 'green lighten-4' : item.event_type.name === 'Party' ? 'orange lighten-4' : 'blue lighten-4'"
                                    :text-color="item.event_type.name === 'Hen' ? 'pink darken-3' : item.event_type.name === 'Birthday' ? 'green darken-3' : item.event_type.name === 'Party' ? 'orange darken-3' : 'blue darken-3'">{{
                                        item.event_type.name
                                    }} <label v-if="item.event_type.name !== 'Party'" style="white-space:preserve;">
                                        Party</label>
                                </v-chip>
                                <span v-else class="grey--text">—</span>
                            </td>
                            <td>
                                <span v-if="item.location">{{ item.location.name }}</span>
                                <span v-else class="grey--text">—</span>
                            </td>
                            <td>
                                {{ item.advisor_id && getAdvisorById(item.advisor_id)
                                    ? getAdvisorById(item.advisor_id).name
                                    : '—' }}
                            </td>
                            <td @click.stop class="py-1">
                                <div class="d-flex align-center" style="gap: 6px;">
                                    <v-tooltip bottom color="success">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip small label v-bind="attrs" v-on="on"
                                                :color="(item.quotes || []).filter(q => q.event_id !== null).length > 0 ? 'success' : 'grey lighten-3'"
                                                :text-color="(item.quotes || []).filter(q => q.event_id !== null).length > 0 ? 'white' : 'grey'"
                                                class="font-weight-bold px-2">
                                                <v-icon small left>mdi-calendar-check</v-icon>
                                                {{(item.quotes || []).filter(q => q.event_id !== null).length}}
                                            </v-chip>
                                        </template>
                                        <span>Booked quotes</span>
                                    </v-tooltip>
                                    <v-tooltip bottom color="grey darken-2">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip small label v-bind="attrs" v-on="on"
                                                :color="(item.quotes || []).filter(q => q.event_id === null).length > 0 ? 'blue-grey darken-1' : 'grey lighten-3'"
                                                :text-color="(item.quotes || []).filter(q => q.event_id === null).length > 0 ? 'white' : 'grey'"
                                                class="font-weight-bold px-2">
                                                <v-icon small left>mdi-file-document-outline</v-icon>
                                                {{(item.quotes || []).filter(q => q.event_id === null).length}}
                                            </v-chip>
                                        </template>
                                        <span>Pending quotes</span>
                                    </v-tooltip>
                                </div>
                            </td>
                            <td @click.stop style="text-align: center;">
                                <v-icon v-if="hasOrganiserDepositPayment(item)" color="success"
                                    @click="openPaymentDetailsDialog(item)" style="cursor: pointer;"
                                    title="View payment details">
                                    mdi-check-circle
                                </v-icon>
                            </td>
                            <td v-if="get_user.id == 1 || get_user.id == 30" @click.stop>
                                <v-tooltip bottom color="primary">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn small icon v-bind="attrs" v-on="on" @click="eventMoveToEdit(item)">
                                            <v-icon small color="primary">mdi-pen</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit Lead</span>
                                </v-tooltip>
                                <v-tooltip bottom :color="canDeleteLead(item) ? 'error' : 'grey darken-2'">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn small icon v-bind="attrs" v-on="on" :disabled="!canDeleteLead(item)"
                                            @click="eventOpenConfirmDeleteLeadDialog(item)">
                                            <v-icon small
                                                :color="canDeleteLead(item) ? 'error' : 'grey'">mdi-delete</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ getDeleteTooltip(item) }}</span>
                                </v-tooltip>
                            </td>
                        </tr>
                    </template>
                    <template v-slot:no-data>
                        <div class="py-10 text-center">
                            <v-icon size="48" color="grey lighten-2">mdi-account-search-outline</v-icon>
                            <div class="mt-2 grey--text">No leads found</div>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <div class="sticky-pagination" v-if="page_count > 1">
            <v-pagination v-model="page" :length="page_count" :total-visible="7" @input="fetchLeads"></v-pagination>
        </div>

        <CreateNewLead :show_create_new_lead_dialog="lead_components.show_create_new_lead_dialog"
            @eventCloseCreateNewLead="eventCloseCreateNewLead()" @eventLeadCreated="eventLeadCreated()"></CreateNewLead>

        <ConfirmDeleteLead :show_confirm_delete_lead_dialog="lead_components.show_confirm_delete_lead_dialog"
            :lead_id="lead_id" @eventCloseConfirmDeleteLeadDialog="eventCloseConfirmDeleteLeadDialog()"
            @eventLeadDeleted="eventLeadDeleted()">
        </ConfirmDeleteLead>

        <v-dialog v-model="payment_details_dialog" max-width="600px">
            <v-card>
                <v-card-title class="text-h5 primary white--text">
                    <v-icon left color="white">mdi-cash-check</v-icon>
                    Payment Details
                </v-card-title>
                <v-card-text class="pt-4">
                    <v-list>
                        <div v-for="(payment, index) in selected_payments" :key="index">
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-subtitle>Quote Name</v-list-item-subtitle>
                                    <v-list-item-title class="text-h6">{{ payment.quote_name }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-subtitle>Total Paid Amount</v-list-item-subtitle>
                                    <v-list-item-title class="text-h6">{{ get_selected_company.currency }}{{
                                        payment.total_paid_amount.toFixed(2) }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-subtitle>Payment Date</v-list-item-subtitle>
                                    <v-list-item-title>{{ $date(payment.payment_date).format('DD-MMM-YYYY')
                                    }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-subtitle>Payment Gateway</v-list-item-subtitle>
                                    <v-list-item-title>{{ payment.payment_gateway }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item v-if="payment.reference_code">
                                <v-list-item-content>
                                    <v-list-item-subtitle>Reference Code</v-list-item-subtitle>
                                    <v-list-item-title>{{ payment.reference_code }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider v-if="index < selected_payments.length - 1" class="my-3"></v-divider>
                        </div>
                    </v-list>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="payment_details_dialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

// All Components added here
import CreateNewLead from "./components/CreateNewLead.vue";
import ConfirmDeleteLead from "./components/ConfirmDeleteLead.vue";
import pageBadgesMixin from "@/common/PageNameStore/pageBadgesMixin";
import { lookups, loadLookups, advisorById } from "@/common/companyLookups";

export default {
    mixins: [pageBadgesMixin],
    data: () => ({
        headers: [
            { text: 'ID', value: 'id', align: 'center', width: '70px', sortable: false },
            { text: 'Created', value: 'created_at', sortable: false },
            { text: 'Group Leader', value: 'group_leader', sortable: false },
            { text: 'Mobile', value: 'phone', sortable: false },
            { text: 'Email', value: 'email', sortable: false },
            { text: 'Party Date', value: 'start_date', sortable: false },
            { text: 'Size', value: 'people', align: 'center', width: '70px', sortable: false },
            { text: 'Event Type', value: 'event_type.name', width: '120px', sortable: false },
            { text: 'Destination', value: 'location.name', sortable: false },
            { text: 'Advisor', value: 'advisor', sortable: false },
            { text: 'Quotes', value: 'quotes', sortable: false, width: '120px' },
            { text: 'Paid', value: 'paid', sortable: false, align: 'center', width: '60px' },
            { text: 'Action', value: 'action', sortable: false, width: '90px' },
        ],
        search_query: '',
        filter_advisor: null,
        filter_event_type: null,
        filter_destination: null,
        filter_paid: null,
        paidOptions: [
            { text: 'Paid', value: 'paid' },
            { text: 'Unpaid', value: 'unpaid' },
        ],
        lead_components: {
            show_create_new_lead_dialog: false,
            show_confirm_delete_lead_dialog: false
        },
        lead_id: null,
        freedom_bookings_file: null,
        leads: [],
        leads_total: 0,
        leads_loading: false,
        current_page: 1,
        page: 1,
        _search_timer: null,
        _ignoring_filters: false,
        payment_details_dialog: false,
        selected_payments: [],
    }),
    components: {
        CreateNewLead,
        ConfirmDeleteLead
    },
    created() {
        this.setPageName('Leads')
    },
    computed: {
        getAdvisors() {
            return lookups.advisors
        },
        getEventTypes() {
            return lookups.event_types
        },
        getLocations() {
            return lookups.locations
        },
        advisorOptions() {
            return this.getAdvisors
        },
        eventTypeOptions() {
            return (this.getEventTypes || []).map(et => et.name).sort()
        },
        destinationOptions() {
            return (this.getLocations || []).map(l => l.name).sort()
        },
        hasActiveFilters() {
            return !!(this.search_query || this.filter_advisor !== null || this.filter_event_type || this.filter_destination || this.filter_paid)
        },
        page_count() {
            return Math.ceil(this.leads_total / 15) || 1
        },
        pageBadges() {
            return [this.leads_total.toLocaleString()]
        },
        ...mapGetters({
            get_user: 'auth/get_user',
            get_selected_company: 'auth/get_selected_company',
        })
    },
    watch: {
        filter_advisor() { if (!this._ignoring_filters) this.resetAndFetch() },
        filter_event_type() { if (!this._ignoring_filters) this.resetAndFetch() },
        filter_destination() { if (!this._ignoring_filters) this.resetAndFetch() },
        filter_paid() { if (!this._ignoring_filters) this.resetAndFetch() },
        search_query() {
            if (this._ignoring_filters) return
            clearTimeout(this._search_timer)
            this._search_timer = setTimeout(() => this.resetAndFetch(), 350)
        },
    },
    mounted() {
        this.fetchLeads()
        loadLookups('advisors', 'locations', 'event_types')
    },
    methods: {
        ...mapActions({
            setPageName: 'pageNameStore/setPageNameAction',
        }),
        getAdvisorById(advisor_id) {
            return advisorById(advisor_id)
        },
        // Called by the AI Assistant after it changes data: same page, filters and search
        aiRefresh() {
            return this.fetchLeads(this.current_page)
        },
        async fetchLeads(page = 1) {
            this.leads_loading = true
            this.current_page = page
            try {
                const params = { page, per_page: 15 }
                if (this.search_query) params.search = this.search_query
                if (this.filter_advisor !== null) params.advisor_id = this.filter_advisor
                if (this.filter_event_type) params.event_type = this.filter_event_type
                if (this.filter_destination) params.destination = this.filter_destination
                if (this.filter_paid) params.filter_paid = this.filter_paid
                const response = await this.$axios.get(
                    `admin/leads/get_leads_paginated/${this.get_selected_company.id}`,
                    params
                )
                if (response.data?.response) {
                    this.leads = response.data.data
                    this.leads_total = response.data.total
                } else {
                    this.leads = []
                    this.leads_total = 0
                }
            } catch (_) {
                this.leads = []
                this.leads_total = 0
            }
            this.leads_loading = false
        },
        resetAndFetch() {
            this.page = 1
            this.fetchLeads(1)
        },
        clearFilters() {
            this._ignoring_filters = true
            this.search_query = ''
            this.filter_advisor = null
            this.filter_event_type = null
            this.filter_destination = null
            this.filter_paid = null
            this._ignoring_filters = false
            this.page = 1
            this.fetchLeads(1)
        },
        eventOpenCreateNewLeadDialog() {
            this.lead_components.show_create_new_lead_dialog = true
        },
        eventCloseCreateNewLead() {
            this.lead_components.show_create_new_lead_dialog = false
        },
        async eventLeadCreated() {
            this.lead_components.show_create_new_lead_dialog = false
            await this.fetchLeads()
        },
        eventOpenConfirmDeleteLeadDialog(item) {
            this.lead_id = item.id
            this.lead_components.show_confirm_delete_lead_dialog = true
        },
        eventCloseConfirmDeleteLeadDialog() {
            this.lead_components.show_confirm_delete_lead_dialog = false
        },
        async eventLeadDeleted() {
            this.lead_components.show_confirm_delete_lead_dialog = false
            await this.fetchLeads()
        },
        eventMoveToEdit(item) {
            this.$router.push({ name: 'EditLeads', params: { id: item.id } })
        },
        canDeleteLead(item) {
            // Check if lead has payments
            if (item.payments && item.payments.length > 0) {
                return false
            }
            // Check if lead's event has invitees with payments
            if (item.event && item.event.invitees) {
                const hasInviteePayments = item.event.invitees.some(invitee =>
                    invitee.payments && invitee.payments.length > 0
                )
                if (hasInviteePayments) {
                    return false
                }
            }
            return true
        },
        getDeleteTooltip(item) {
            if (!this.canDeleteLead(item)) {
                // Check which type of payments exist
                const hasLeadPayments = item.payments && item.payments.length > 0
                const hasInviteePayments = item.event && item.event.invitees &&
                    item.event.invitees.some(invitee => invitee.payments && invitee.payments.length > 0)

                if (hasLeadPayments && hasInviteePayments) {
                    return 'Cannot delete: Lead and invitee payments exist'
                } else if (hasLeadPayments) {
                    return 'Cannot delete: Lead payments exist'
                } else if (hasInviteePayments) {
                    return 'Cannot delete: Invitee payments exist'
                }
            }
            return 'Delete Lead'
        },
        hasOrganiserDepositPayment(item) {
            return (item.quotes || []).some(quote => quote.payments && quote.payments.length > 0)
        },
        openPaymentDetailsDialog(item) {
            const payments = []
                ; (item.quotes || []).forEach(quote => {
                    if (quote.payments && quote.payments.length > 0) {
                        quote.payments.forEach(payment => {
                            let reference_code = ''
                            try {
                                const payment_collection = JSON.parse(payment.payment_collection)
                                reference_code = payment_collection.reference_code || ''
                            } catch (e) {
                                // ignore
                            }
                            payments.push({
                                quote_name: quote.name,
                                total_paid_amount: payment.total_paid_amount,
                                payment_date: payment.payment_date,
                                payment_gateway: payment.payment_gateway,
                                reference_code: reference_code
                            })
                        })
                    }
                })
            this.selected_payments = payments
            this.payment_details_dialog = true
        },
        async importFreedomBookings() {
            if (this.freedom_bookings_file) {
                const formData = new FormData()
                formData.append('file', this.freedom_bookings_file)
                formData.append('company_id', this.get_selected_company.id)
                await this.$axios.post('admin/leads/import_leads', formData)
                    .then(({ data }) => {
                        console.log(data)
                        this.freedom_bookings_file = null
                    })
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.card-section-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.card-section-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
}

.header-filter-input ::v-deep .v-input__control {
    background: white;
    border-radius: 4px;
}

.header-clear-row {
    margin: 6px 0 -8px -8px;
}

.header-clear-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: none;
    color: white !important;
}

.header-outline-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    color: white !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}

.header-create-btn {
    background: white !important;
    color: #3988AA !important;
}

.leads-header-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 13px !important;
    text-transform: none;
    height: 36px !important;
    padding: 0 14px !important;
}

.leads-action-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 13px !important;
    text-transform: none;
}

.leads-table-card {
    border-radius: 10px !important;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
    overflow: hidden;
    margin-bottom:100px;
}

.leads-table {
    font-family: Manrope;

    .v-data-footer {
        background-color: #F9F9F9;
        color: #7C7C7C;
        font-family: Manrope;
        border-top: 1px solid #E9E9E9;
    }
}

.sticky-pagination {
    position: fixed;
    bottom: 20px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    z-index: 100;
    pointer-events: none;

    ::v-deep .v-pagination {
        pointer-events: all;
        background: white;
        border-radius: 8px;
        padding: 4px 8px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    }
}

.lead-name-link {
    color: #3988AA;
    text-decoration: underline;
    display: inline-block;
}

.lead-row {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #F5FAFD !important;
    }

    td {
        font-family: Manrope;
        font-size: 0.875rem;
    }
}
</style>